import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, ArrowRight, ChevronRight, Home, Calendar, Clock, Tag } from 'lucide-react';
import SEO, { buildArticleSchema, buildBreadcrumbSchema } from '../components/SEO';
import { BLOG_ARTICLES, SITE_URL, BRAND } from '../lib/seoContent';

/** Blog index page - lists all articles */
export const BlogIndex = () => {
  const url = `${SITE_URL}/blog`;
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: `${BRAND} — Journal`,
      url,
      publisher: { '@type': 'Organization', name: BRAND, url: `${SITE_URL}/` },
      blogPost: BLOG_ARTICLES.map((a) => ({
        '@type': 'BlogPosting',
        headline: a.title,
        datePublished: a.published,
        url: `${SITE_URL}/blog/${a.slug}`,
      })),
    },
    buildBreadcrumbSchema([
      { name: 'Home', url: `${SITE_URL}/` },
      { name: 'Journal', url },
    ]),
  ];

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="blog-index-page">
      <SEO
        title={`CriLam Journal — Technology, Materials & Interior Insights | ${BRAND}`}
        description="Deep-dive articles on CriLam PUR-Bond™ technology, 6H acrylic hardness, mirror finish science, and anti-yellowing chemistry — from the manufacturer of India's premier Pre-Laminated Acrylic Boards."
        canonical={url}
        keywords="CriLam blog, acrylic laminate blog, CriLam PUR-Bond™ technology articles, interior technology India"
        jsonLd={jsonLd}
      />
      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/40 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-5xl">
          <nav className="mb-6 text-xs text-white/50 flex items-center gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37] flex items-center gap-1"><Home className="h-3 w-3" /> Home</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white/80">Journal</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-xs font-bold uppercase tracking-wider mb-6">
            <BookOpen className="h-3.5 w-3.5" /> The CriLam Journal
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Outfit'] leading-tight">
            Technology · Materials · Craft
          </h1>
          <p className="text-lg text-white/70 max-w-2xl">
            Deep-dive articles on CriLam PUR-Bond™ technology, 6H acrylic hardness, mirror-finish science, and anti-yellowing chemistry from CriLam Decorative.
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-14">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-6">
            {BLOG_ARTICLES.map((a, i) => (
              <motion.div
                key={a.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link
                  to={`/blog/${a.slug}`}
                  className="block bg-white rounded-2xl border border-gray-200 p-6 hover:border-[#D4AF37] hover:shadow-lg transition-all h-full"
                  data-testid={`blog-card-${a.slug}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#D4AF37]/10 text-[#7A6621] text-xs font-bold rounded-full">
                      <Tag className="h-3 w-3" /> {a.category}
                    </span>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {a.readTime}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-[#004534] mb-2 font-['Outfit'] leading-snug">{a.title}</h2>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">{a.excerpt}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {a.published}
                    </span>
                    <span className="text-sm text-[#D4AF37] font-semibold flex items-center gap-1">
                      Read <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

/** Individual blog post renderer */
export const BlogPost = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const post = BLOG_ARTICLES.find((a) => a.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F7F8F5] flex items-center justify-center px-6" data-testid="blog-post-not-found">
        <div className="text-center max-w-md">
          <BookOpen className="h-12 w-12 text-[#004534] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#004534] mb-3">Post not found</h1>
          <Link to="/blog" className="text-[#D4AF37] font-semibold hover:underline">Back to Journal →</Link>
        </div>
      </div>
    );
  }

  const url = `${SITE_URL}/blog/${post.slug}`;
  const jsonLd = [
    buildArticleSchema({
      headline: post.title,
      description: post.excerpt,
      url,
      datePublished: post.published,
      dateModified: post.modified,
    }),
    buildBreadcrumbSchema([
      { name: 'Home', url: `${SITE_URL}/` },
      { name: 'Journal', url: `${SITE_URL}/blog` },
      { name: post.title, url },
    ]),
  ];

  const related = BLOG_ARTICLES.filter((a) => a.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid={`blog-post-${post.slug}`}>
      <SEO
        title={`${post.title} | CriLam Journal`}
        description={post.excerpt}
        canonical={url}
        keywords={`${post.category}, CriLam blog, ${post.title}`}
        jsonLd={jsonLd}
        ogType="article"
        articlePublished={post.published}
        articleModified={post.modified}
      />

      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-14 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/40 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-3xl">
          <nav className="mb-6 text-xs text-white/50 flex items-center gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37] flex items-center gap-1"><Home className="h-3 w-3" /> Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/blog" className="hover:text-[#D4AF37]">Journal</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white/80 truncate">{post.title}</span>
          </nav>
          <div className="flex items-center gap-3 mb-5">
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#D4AF37] text-[#0A0D0C] text-xs font-bold rounded-full">
              <Tag className="h-3 w-3" /> {post.category}
            </span>
            <span className="text-xs text-white/60 flex items-center gap-1"><Clock className="h-3 w-3" /> {post.readTime}</span>
            <span className="text-xs text-white/60 flex items-center gap-1"><Calendar className="h-3 w-3" /> {post.published}</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 font-['Outfit'] leading-tight">{post.title}</h1>
          <p className="text-lg text-white/70">{post.excerpt}</p>
        </div>
      </section>

      {/* Body */}
      <article className="py-14">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed" style={{ whiteSpace: 'pre-line' }}>
            {post.content}
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-12 bg-white border-y">
          <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
            <h3 className="text-2xl font-bold text-[#004534] mb-6 font-['Outfit']">More from the Journal</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="block bg-[#F7F8F5] rounded-xl border border-gray-200 p-5 hover:border-[#D4AF37] transition-colors"
                  data-testid={`related-post-${r.slug}`}
                >
                  <p className="text-xs text-[#D4AF37] font-bold uppercase tracking-wider mb-2">{r.category}</p>
                  <h4 className="font-bold text-[#004534] mb-1 font-['Outfit'] leading-snug">{r.title}</h4>
                  <p className="text-xs text-gray-500 mt-2">{r.readTime}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-14 bg-gradient-to-br from-[#004534] to-[#0A0D0C] text-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-4 font-['Outfit']">See the Technology in Action</h3>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Link to="/ai-visualizer" data-testid="blog-cta-visualizer">
              <button className="px-6 py-3 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                AI Visualizer <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <Link to="/nail-free-advantage">
              <button className="px-6 py-3 border-2 border-white/40 text-white font-medium rounded-lg hover:bg-white/10">
                Nail-Free Advantage
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogIndex;
